/**
 * Canvas Pool — reuses a small set of off-screen canvases to avoid
 * creating (and leaking) new ones every detection frame.
 *
 * Safari enforces a hard per-page canvas limit; without pooling the
 * detection loop creates ~33 canvases/sec and crashes within 1-2 min.
 */

const POOL_SIZE = 4;

export function createCanvasPool() {
  const canvases = [];
  const available = [];

  for (let i = 0; i < POOL_SIZE; i++) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const entry = { canvas, ctx, inUse: false };
    canvases.push(entry);
    available.push(entry);
  }

  return {
    /**
     * Borrow a canvas from the pool, resized to (w, h).
     * Returns { canvas, ctx }.  Call release() when done.
     */
    acquire(w, h) {
      let entry = available.pop();
      if (!entry) {
        // All checked out — reuse the first one (caller should release promptly)
        entry = canvases[0];
      }
      entry.inUse = true;
      entry.canvas.width = w;
      entry.canvas.height = h;
      return entry;
    },

    /**
     * Return a previously acquired entry back to the pool.
     */
    release(entry) {
      if (!entry) return;
      entry.inUse = false;
      if (!available.includes(entry)) {
        available.push(entry);
      }
    },

    /**
     * Destroy all canvases (call on component unmount).
     */
    destroy() {
      for (const entry of canvases) {
        entry.canvas.width = 0;
        entry.canvas.height = 0;
      }
      canvases.length = 0;
      available.length = 0;
    },
  };
}
